import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";

/**
 * Iter-158.0 — Multi-Department Beta gate.
 *
 * Provides `useBeta()` returning `{ hasBetaAccess, loading }`. Polls
 * `GET /api/beta/me` once per authed session. The endpoint is safe for
 * every user (no 403), so non-beta users simply resolve to
 * `hasBetaAccess === false` and never see any beta UI.
 *
 * The provider mounts BELOW <AuthProvider> so it can react to auth
 * transitions. When the user logs out we reset to guest state.
 */
const BetaContext = createContext({ hasBetaAccess: false, loading: true });

export function BetaProvider({ children }) {
    const { status } = useAuth();
    const [hasBetaAccess, setHasBetaAccess] = useState(false);
    const [loading, setLoading] = useState(true);

    const refresh = useCallback(async () => {
        if (status === "loading") {
            // Auth is still bootstrapping — keep loading=true so
            // BetaGate does not prematurely redirect. Another refresh
            // will fire once status transitions to authed/guest.
            setLoading(true);
            return;
        }
        if (status !== "authed") {
            setHasBetaAccess(false);
            setLoading(false);
            return;
        }
        try {
            const { data } = await api.get("/beta/me", { timeout: 10000 });
            setHasBetaAccess(Boolean(data?.has_beta_access));
        } catch {
            // Endpoint is safe for all users — a failure here is
            // transient (network / 5xx). Default to no access rather
            // than exposing the beta UI on a soft failure.
            setHasBetaAccess(false);
        } finally {
            setLoading(false);
        }
    }, [status]);

    useEffect(() => {
        setLoading(true);
        refresh();
    }, [refresh]);

    return (
        <BetaContext.Provider value={{ hasBetaAccess, loading, refresh }}>
            {children}
        </BetaContext.Provider>
    );
}

export function useBeta() {
    return useContext(BetaContext);
}
